/**
 * useScreenshot Hook
 * 
 * Capture screenshot PNG dengan:
 * - Custom resolution support
 * - Transparent background option
 * - Active camera support (default atau GLB)
 * 
 * @param {Object} refs - Renderer, scene, camera refs
 * @param {Object} options - Screenshot options
 * @returns {Function} captureScreenshot method
 */

import { useCallback } from 'react'
import { getActiveCamera } from '@utils/raycasting'

export function useScreenshot(refs, options = {}) {
  const {
    rendererRef,
    sceneRef,
    cameraRef,
    selectedCameraRef,
    modelRef,
  } = refs

  const {
    useCustomResolution = false,
    exportWidth = 1920,
    exportHeight = 1080,
    transparentBackground = false,
  } = options

  const captureScreenshot = useCallback(() => {
    if (!rendererRef.current || !sceneRef.current || !cameraRef.current) return

    try {
      const width = useCustomResolution
        ? exportWidth
        : rendererRef.current.domElement.width
      const height = useCustomResolution
        ? exportHeight
        : rendererRef.current.domElement.height

      // Store original background settings - EXACT COPY from BACKUP line 5360-5361
      const originalClearAlpha = rendererRef.current.getClearAlpha()
      const originalBgColor = sceneRef.current.background

      // Apply transparent background if enabled - EXACT COPY from BACKUP line 5364-5367
      if (transparentBackground) {
        rendererRef.current.setClearAlpha(0) // Transparent
        sceneRef.current.background = null // Remove background
      }

      // Get active camera - use helper function
      const activeCamera = getActiveCamera(cameraRef, selectedCameraRef, modelRef)

      if (useCustomResolution) {
        // Store original size and settings - EXACT COPY from BACKUP line 5387-5390
        const originalWidth = rendererRef.current.domElement.width
        const originalHeight = rendererRef.current.domElement.height
        const originalAspect = activeCamera.aspect
        const originalPixelRatio = rendererRef.current.getPixelRatio()

        // Temporarily resize renderer and camera - EXACT COPY from BACKUP line 5393-5396
        rendererRef.current.setSize(width, height, false) // false = don't update style
        rendererRef.current.setPixelRatio(1) // Use 1:1 for exact resolution
        activeCamera.aspect = width / height
        activeCamera.updateProjectionMatrix()

        // Render one frame at new resolution - EXACT COPY from BACKUP line 5399
        rendererRef.current.render(sceneRef.current, activeCamera)

        // Capture screenshot - EXACT COPY from BACKUP line 5402-5432
        const canvas = rendererRef.current.domElement
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
          const alphaTag = transparentBackground ? '-alpha' : ''
          link.download = `3d-render-${width}x${height}${alphaTag}-${timestamp}.png`
          link.href = url
          link.click()
          URL.revokeObjectURL(url)

          console.log(
            `Screenshot saved successfully at ${width}x${height}${transparentBackground ? ' with transparent background' : ''}`
          )

          // Restore original size and settings
          rendererRef.current.setSize(originalWidth, originalHeight, false)
          rendererRef.current.setPixelRatio(originalPixelRatio)
          activeCamera.aspect = originalAspect
          activeCamera.updateProjectionMatrix()

          // Restore background settings
          rendererRef.current.setClearAlpha(originalClearAlpha)
          sceneRef.current.background = originalBgColor
        }, 'image/png')
      } else {
        // Use current renderer at viewport resolution - EXACT COPY from BACKUP line 5434-5457
        rendererRef.current.render(sceneRef.current, activeCamera)

        const canvas = rendererRef.current.domElement
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
          const alphaTag = transparentBackground ? '-alpha' : ''
          link.download = `3d-render${alphaTag}-${timestamp}.png`
          link.href = url
          link.click()
          URL.revokeObjectURL(url)

          console.log(
            `Screenshot saved successfully${transparentBackground ? ' with transparent background' : ''}`
          )

          // Restore background settings
          rendererRef.current.setClearAlpha(originalClearAlpha)
          sceneRef.current.background = originalBgColor
        }, 'image/png')
      }
    } catch (error) {
      console.error('Error capturing screenshot:', error)
      alert('Failed to capture screenshot')
    }
  }, [
    rendererRef,
    sceneRef,
    cameraRef,
    selectedCameraRef,
    modelRef,
    useCustomResolution,
    exportWidth,
    exportHeight,
    transparentBackground,
  ])

  return { captureScreenshot }
}

